import React, { ReactNode } from 'react';

import cx from 'classnames';

import { getStatusVariant } from '@shared/utils';
import {
	DateTime,
	Tooltip,
	Badge,
	InteractiveElement,
} from '@shared/components';
import { TickWithBorder } from '../../../assets';
import { DATA_TABLE_DATE_COLUMN } from '@shared/constants';

import styles from './ColumnRenderer.module.scss';

export type TColumnRendererProps =
	// With Copy
	| {
			copyEnabled: true;
			type: 'text';
			primaryValue?: string | number;
			toolTipText: string;
			prefixIcon?: ReactNode;
			prefixImgSrc?: string;
			onClick?: void;
			onPrefixIconClick?: void;
			secondaryValue?: null;
			suffixIcon?: null;
			suffixImgSrc?: string;
			isMaxWidthRequired?: boolean;
			customValue?: ReactNode;
			textColumnClassName?: string;
			isUTC?: boolean;
	  }
	// Without Copy
	| {
			copyEnabled?: false;
			type: 'text' | 'date' | 'multiline' | 'status' | 'icon' | 'custom';
			primaryValue?: string | number;
			secondaryValue?: string | number;
			toolTipText?: string;
			prefixIcon?: ReactNode;
			prefixImgSrc?: string;
			suffixIcon?: ReactNode;
			isMaxWidthRequired?: boolean;
			suffixImgSrc?: string;
			customValue?: ReactNode;
			onClick?: (
				event: React.MouseEvent<HTMLElement, MouseEvent>,
				data?: string | number,
			) => void;
			onPrefixIconClick?: (
				event: React.MouseEvent<HTMLElement, MouseEvent>,
				data: string | number,
			) => void;
			textColumnClassName?: string;
			isUTC?: boolean;
	  };

const ColumnRenderer = (props: TColumnRendererProps) => {
	const {
		type,
		primaryValue = '',
		copyEnabled,
		toolTipText,
		prefixImgSrc,
		prefixIcon = null,
		suffixIcon = null,
		onClick,
		isMaxWidthRequired = false,
		customValue = null,
		secondaryValue,
		textColumnClassName = '',
		isUTC = false,
	} = props;

	const prefixImage =
		prefixImgSrc || prefixIcon ? (
			<div className={styles.prefixImageWrapper}>
				{prefixImgSrc ? (
					<img
						className={styles.prefixImage}
						src={prefixImgSrc}
						alt="prefix-img"
					/>
				) : (
					prefixIcon
				)}
			</div>
		) : null;

	const tooltipTitle = (
		<div className={styles.tooltipTitleWrapper}>
			{copyEnabled && <TickWithBorder width={16} height={16} />}
			<span className={styles.toolTiptextStyle}>{toolTipText}</span>
		</div>
	);

	const onColumnClick: (
		event: React.MouseEvent<HTMLElement, MouseEvent>,
	) => void = (e) => {
		onClick?.(e, primaryValue);
	};

	const TextColumnRenderer = (
		<div className={cx(styles.tooltipSelector, textColumnClassName)}>
			{prefixImage && (
				<InteractiveElement
					type="span"
					onClick={onColumnClick}
					className={styles.icon}
				>
					{prefixImage}
				</InteractiveElement>
			)}
			<InteractiveElement
				type="span"
				onClick={onColumnClick}
				className={cx(
					{ [styles.text]: !isMaxWidthRequired },
					{ [styles.comment]: isMaxWidthRequired },
				)}
			>
				{primaryValue}
			</InteractiveElement>
			{suffixIcon !== null && suffixIcon}
		</div>
	);

	const handleColumnRender = () => {
		switch (type) {
			case 'date':
				return (
					<div className={styles.dateWrapper} data-id={DATA_TABLE_DATE_COLUMN}>
						<DateTime format="MMM DD, YYYY" isUTC={isUTC}>
							{primaryValue}
						</DateTime>
						<DateTime className={styles.time} format="hh:mm A" isUTC={isUTC}>
							{primaryValue}
						</DateTime>
					</div>
				);
			case 'multiline':
				return (
					<div className={styles.multilineWrapper}>
						<span className={styles.title}>{primaryValue}</span>
						<span className={styles.subtitle}>{secondaryValue}</span>
					</div>
				);
			case 'custom':
				return customValue;
			case 'text':
			case 'icon':
				return toolTipText ? (
					<Tooltip placement="top" title={tooltipTitle}>
						{TextColumnRenderer}
					</Tooltip>
				) : (
					<>{TextColumnRenderer}</>
				);
			case 'status':
				return (
					<Badge variant={getStatusVariant(primaryValue)}>{primaryValue}</Badge>
				);
		}
	};

	return <>{handleColumnRender()}</>;
};

export default ColumnRenderer;
